<template>
  <v-card class="dr">
    <header class="dr__head">
      <div class="dr__head-text">
        <p class="dr__eyebrow">Trámites rechazados</p>
        <h2 class="dr__title">Comprobante del rechazo</h2>
        <p class="dr__scope">
          <template v-if="code">
            Consolidado <strong>{{ code }}</strong> ·
            {{ targetIds.length }} facturas
          </template>
          <template v-else-if="invoiceNumber">
            Factura <strong>{{ invoiceNumber }}</strong>
          </template>
        </p>
      </div>
      <button
        type="button"
        class="dr__close"
        title="Cerrar"
        @click="$emit('close-reason')"
      >
        <v-icon small>close</v-icon>
      </button>
    </header>

    <div class="dr__body">
      <!-- 1. Motivo -->
      <section class="dr__section">
        <p class="dr__label">Motivo del rechazo</p>
        <textarea
          v-model="form.observations"
          class="dr__textarea"
          rows="3"
          placeholder="Explica qué debe corregirse."
        ></textarea>
      </section>

      <!-- 2. Comprobante -->
      <section class="dr__section">
        <p class="dr__label">
          Comprobante
          <span class="dr__req">obligatorio</span>
        </p>
        <upload-btn
          multiple
          dropzone
          hint="Cualquier formato. Puedes adjuntar varios."
          :invalid="submitted && !hasFiles"
          @clicked="onFilePicked"
        ></upload-btn>
        <p v-if="submitted && !hasFiles" class="dr__error">
          Adjunta al menos un comprobante.
        </p>
        <p v-else class="dr__help">
          Se suma al expediente; no reemplaza los documentos anteriores.
        </p>
      </section>

      <!-- 3. Expediente -->
      <section class="dr__section">
        <p class="dr__label">
          Expediente del trámite
          <span class="dr__count">{{ documents.length }}</span>
        </p>
        <p v-if="loadingDocs" class="dr__help">Cargando expediente…</p>
        <p v-else-if="!documents.length" class="dr__help">
          Sin documentos previos. Este será el primero.
        </p>
        <ol v-else class="dr__log">
          <li v-for="doc in documents" :key="doc.id" class="dr__log-item">
            <span class="dr__log-date">{{ dateOf(doc.createdAt) }}</span>
            <span class="dr__tag" :class="stepClass(doc.step)">
              {{ stepLabel(doc.step) }}
            </span>
            <a
              :href="fullPath(doc.filePath)"
              target="_blank"
              rel="noopener"
              class="dr__log-name"
              :title="doc.originalName || doc.filePath"
            >
              {{ doc.originalName || doc.filePath }}
            </a>
            <span v-if="doc.uploadedBy" class="dr__log-who">
              {{ doc.uploadedBy.firstName }} {{ doc.uploadedBy.lastName }}
            </span>
          </li>
        </ol>
      </section>
    </div>

    <footer class="dr__foot">
      <button
        type="button"
        class="dr__btn dr__btn--ghost"
        @click="$emit('close-reason')"
      >
        Cancelar
      </button>
      <button
        type="button"
        class="dr__btn dr__btn--save"
        :disabled="saving"
        @click="handleSave"
      >
        <v-icon small class="dr__btn-icon">save</v-icon>
        {{ saving ? 'Guardando…' : 'Guardar' }}
      </button>
    </footer>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'
import UploadButton from './FileUpload'
import { getFullPath } from '@/helpers/TransitHelper'

export default {
  components: {
    'upload-btn': UploadButton,
  },
  props: {
    // { invoiceId: [ids], invoiceNumber?, code?, observations }
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    form: {
      invoiceId: [],
      observations: '',
      files: null,
    },
    documents: [],
    loadingDocs: false,
    // Los errores aparecen al intentar guardar, no al abrir.
    submitted: false,
    saving: false,
  }),
  computed: {
    hasFiles() {
      return Array.isArray(this.form.files) && this.form.files.length > 0
    },
    targetIds() {
      const ids = this.item.invoiceId
      return Array.isArray(ids) ? ids : [ids]
    },
    code() {
      return this.item.code || null
    },
    invoiceNumber() {
      return this.item.invoiceNumber || null
    },
  },
  created() {
    this.form = {
      invoiceId: this.targetIds,
      observations: this.item.observations || '',
      files: null,
    }
    this.loadDocuments()
  },
  methods: {
    fullPath: getFullPath,
    onFilePicked(files) {
      this.form.files = files
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    stepLabel(step) {
      const labels = {
        DENIED: 'Rechazo',
        PAYED: 'Pago',
        DELIVERED: 'Entrega',
        OTHER: 'Otro',
      }
      return labels[step] || step
    },
    stepClass(step) {
      return `dr__tag--${String(step).toLowerCase()}`
    },
    loadDocuments() {
      if (!this.targetIds.length) return
      this.loadingDocs = true
      this.$store
        .dispatch('invoices/getDocuments', { ids: this.targetIds.join(',') })
        .then(docs => {
          // Un soporte subido a un consolidado se guarda una vez por factura
          // con el mismo archivo: en el expediente del trámite va una vez.
          const seen = new Set()
          this.documents = (docs || []).filter(doc => {
            const key = `${doc.step}|${doc.filePath}`
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })
        })
        .finally(() => {
          this.loadingDocs = false
        })
    },
    handleSave() {
      this.submitted = true
      if (!this.hasFiles) return
      this.saving = true
      this.$store
        .dispatch('transit/deniedReason', this.form)
        .then(() => {
          this.$emit('save-reason')
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.dr {
  border-radius: 12px;
  overflow: hidden;
}

/* === Encabezado === */
.dr__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px 24px;
  border-left: 4px solid #b91c1c;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}
.dr__head-text {
  flex: 1;
  min-width: 0;
}
.dr__eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.dr__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}
.dr__scope {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}
.dr__scope strong {
  color: #254f87;
}
.dr__close {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
  transition: background 0.18s ease;
}
.dr__close:hover {
  background: #f1f5f9;
}
.dr__close .v-icon {
  color: #64748b !important;
}

/* === Cuerpo === */
.dr__body {
  padding: 16px 24px 4px;
  max-height: 62vh;
  overflow-y: auto;
}
.dr__section {
  margin-bottom: 18px;
}
.dr__label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
}
.dr__req {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #b91c1c;
}
.dr__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
}
.dr__error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #b91c1c;
}
.dr__help {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

/* === Motivo === */
.dr__textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: #0f172a;
  background: #ffffff;
  resize: vertical;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.dr__textarea:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.15);
}
.dr__textarea::placeholder {
  color: #94a3b8;
}

/* === Expediente === */
.dr__log {
  list-style: none;
  margin: 0;
  padding: 0 0 0 12px;
  border-left: 2px solid #e2e8f0;
  max-height: 150px;
  overflow-y: auto;
}
.dr__log-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 12px;
}
.dr__log-date {
  flex: 0 0 auto;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.dr__tag {
  flex: 0 0 auto;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.dr__tag--denied {
  background: #fee2e2;
  color: #991b1b;
}
.dr__tag--payed {
  background: #dbeafe;
  color: #1e40af;
}
.dr__tag--delivered {
  background: #dcfce7;
  color: #166534;
}
.dr__tag--other {
  background: #f1f5f9;
  color: #475569;
}
.dr__log-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #254f87;
  text-decoration: none;
}
.dr__log-name:hover {
  text-decoration: underline;
}
.dr__log-who {
  flex: 0 0 auto;
  color: #94a3b8;
}

/* === Pie === */
.dr__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.dr__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.18s ease;
}
.dr__btn:focus-visible {
  outline: 2px solid #94a3b8;
  outline-offset: 2px;
}
.dr__btn--ghost {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #475569;
}
.dr__btn--ghost:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
/* Blanco sobre #254f87 = 8.25:1, cumple WCAG AA. */
.dr__btn--save {
  background: #254f87;
  border-color: #254f87;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(12, 27, 48, 0.3),
    0 4px 10px rgba(37, 79, 135, 0.25);
}
.dr__btn--save .dr__btn-icon {
  color: #ffffff !important;
}
.dr__btn--save:hover:not(:disabled) {
  background: #1a3a63;
  border-color: #1a3a63;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(12, 27, 48, 0.35),
    0 8px 18px rgba(37, 79, 135, 0.3);
}
.dr__btn--save:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: inset 0 2px 4px rgba(12, 27, 48, 0.4);
}
.dr__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}
</style>
